// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type {
    ProgramFallbackAcqFee,
    ProgramFallbackDealerInputs,
    ProgramFallbackHold,
    ProgramFallbackRateType,
    ProgramFallbackResidual,
    ProgramFallbackResidualValue,
    ProgramLookupOutcome,
    StateTree
} from '@makemydeal/dr-dash-types';
import { AccelerateVehicleCondition, FINANCE, LEASE, RESIDUAL_DOLLAR, RESIDUAL_PERCENT } from '@makemydeal/dr-platform-types';
import type { CalculationEndpointType, FeeOverride, IDealerFee, OfferType } from '@makemydeal/dr-platform-types';
import { FeesCategoriesType } from '@makemydeal/dr-shared-types';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { ReduxStore } from '@makemydeal/dr-shared-store';
import { LOCKED_PROGRAM_META, utils as offerReduxUtils } from '@makemydeal/dr-offer-redux';

// actions
import { clearManualFieldOverrides } from './manualFieldOverridesActionCreators';
import {
    syncedAcqFeeOverride,
    syncedMaxResidualizedMsrpOverride,
    syncedSecurityDepositOverride,
    updatedFeesOverride,
    withAcqFeeEntry
} from './offerActionCreators';
import {
    partialResetOverrides,
    setProgramFallbackOverrides,
    updateAdjustedResidualOverride,
    updateBaseResidualOverride,
    updateBuyRateOverride,
    updateCurrentCalculationEndpoint,
    updateSellRateOverride
} from './offerRedux';

// action types
import {
    HOLD_PROGRAM_FALLBACK_VALUES,
    MARK_PROGRAM_LOOKUP_PENDING,
    RELEASE_PROGRAM_FALLBACK_VALUES,
    RESOLVE_PROGRAM_LOOKUP,
    DISCARD_PROGRAM_LOOKUP_PENDING
} from '../actionTypes/offerActionTypes';

// constants / selectors
import {
    ACQUISITION_FEE_ID,
    CALC_ENDPOINTS,
    LOAN_ORIGINATION_FEE_ID,
    PROGRAM_LOOKUP_EDIT_META,
    PRO_RATA_FEE_TYPE_ID
} from '../constants';
import { isProgramsSlideOutOpen } from '../selectors/deskingGlobalSelectors';
import { getManualFieldOverrides } from '../selectors/manualFieldOverridesSelectors';
import * as offerReduxSelectors from '../selectors/offerRedux';
import { explicitLenderP1AppliesToOffer, getProgramFallbackHold } from '../selectors/programsSelectors';

export type ProgramOrchestrationStore = Pick<ReduxStore<StateTree>, 'dispatch' | 'getState'>;

// offer-redux keeps these in `constants/payment`, which its barrel does not re-export. Typed to the narrow
// union so a typo is a compile error rather than a rate type that silently fails to match.
const RATE_TYPE_APR: ProgramFallbackRateType = 'APR';
const RATE_TYPE_MONEY_FACTOR: ProgramFallbackRateType = 'MoneyFactor';

export const PROGRAM_QUOTES_REFETCH_META = 'programQuotesRefetch';

/**
 * Marks an action whose dispatcher has already moved the calculation endpoint itself, so
 * `programInvalidationMiddleware` leaves it alone.
 *
 * `DealTypeDropdown` is the one dispatcher that needs this: it owns the endpoint for the Cash, FlexBuy and
 * used-vehicle-Lease transitions, and its `updatedOfferType` is in the middleware's invalidating set, so
 * both would otherwise switch on one transition. Two of those combinations are only blocked today by the
 * order two unrelated reducers happen to run in — see the middleware's own note.
 */
export const CALC_ENDPOINT_OWNED_BY_DISPATCHER_META = 'calcEndpointOwnedByDispatcher';

export type ProgramOrchestrationOptions = {
    stampRequestId?: (action: AnyFSA) => void;
    /**
     * Extra meta for the endpoint-switch action, for a caller whose switch carries something the generic
     * path knows nothing about.
     *
     * The Auto Select checkbox needs it: its CDL tag (`dc-mv-311`) is keyed on `meta.userIntent` plus
     * `meta.analyticsPayload.toggleState`, so routing that dispatch through here without them would drop
     * the tag for the check direction while leaving the uncheck direction reporting.
     */
    switchMeta?: {
        userIntent?: string;
        analyticsPayload?: Record<string, any>;
        downPayment?: number | null;
    };
    /**
     * Whether the endpoint switch carries the payment call. Defaults to true, which is what a caller wants
     * when the switch is the only thing asking for a new price.
     *
     * `changeVehicleMiddleware` sets it false: it re-dispatches the vehicle switch straight afterwards and
     * that action carries the payment. An extra one here would also not be discarded as superseded, because
     * `SWITCH_VEHICLE` is exempt from the payment-sequence guard.
     */
    withPayment?: boolean;
    lockedProgram?: Record<string, any>;
    lookupEdit?: AnyFSA;
};

export type HoldProgramFallbackValuesAction = { type: typeof HOLD_PROGRAM_FALLBACK_VALUES; payload: ProgramFallbackHold };
export type ReleaseProgramFallbackValuesAction = { type: typeof RELEASE_PROGRAM_FALLBACK_VALUES };
export type MarkProgramLookupPendingAction = { type: typeof MARK_PROGRAM_LOOKUP_PENDING; payload: ProgramLookupOutcome };
export type DiscardProgramLookupPendingAction = {
    type: typeof DISCARD_PROGRAM_LOOKUP_PENDING;
    payload: { requestId?: number };
};

export type ResolveProgramLookupAction = {
    type: typeof RESOLVE_PROGRAM_LOOKUP;
    payload: { noProgram: boolean; requestId?: number };
};
export type ProgramOrchestrationActions =
    | HoldProgramFallbackValuesAction
    | ReleaseProgramFallbackValuesAction
    | MarkProgramLookupPendingAction
    | ResolveProgramLookupAction;

const holdFallbackValues = (payload: ProgramFallbackHold): HoldProgramFallbackValuesAction => ({
    type: HOLD_PROGRAM_FALLBACK_VALUES,
    payload
});

const releaseFallbackValues = (): ReleaseProgramFallbackValuesAction => ({ type: RELEASE_PROGRAM_FALLBACK_VALUES });

const markLookupPending = (payload: ProgramLookupOutcome): MarkProgramLookupPendingAction => ({
    type: MARK_PROGRAM_LOOKUP_PENDING,
    payload
});

const discardLookupPending = (requestId?: number): DiscardProgramLookupPendingAction => ({
    type: DISCARD_PROGRAM_LOOKUP_PENDING,
    payload: { requestId }
});

const resolveLookup = (noProgram: boolean, requestId?: number): ResolveProgramLookupAction => ({
    type: RESOLVE_PROGRAM_LOOKUP,
    payload: { noProgram, requestId }
});

/** A value the user typed. `0` is one of those, so only `null`/`undefined` counts as absent. */
const overriddenResidual = (percent?: number, dollar?: number): ProgramFallbackResidualValue | undefined => {
    if (percent != null) return { value: percent, unitType: RESIDUAL_PERCENT };
    if (dollar != null) return { value: dollar, unitType: RESIDUAL_DOLLAR };
    return undefined;
};

/**
 * What the deal is pricing at. These selectors coerce a missing value to `0`, so `0` cannot be told from
 * absent here and is treated as absent — a residual of zero would not price the fallback anyway.
 */
const effectiveResidual = (percent: number, dollar: number): ProgramFallbackResidualValue | undefined => {
    if (percent) return { value: percent, unitType: RESIDUAL_PERCENT };
    if (dollar) return { value: dollar, unitType: RESIDUAL_DOLLAR };
    return undefined;
};

/**
 * The lease residuals a fallback will need, preferring what the user typed over what the deal is pricing at
 * — the same order the rate uses — and percent over dollar.
 *
 * Percent first because a dollar residual belongs to the term being left. The fallback is happening because
 * no program covers the new term, so a percent survives the change where a dollar figure would carry the
 * old term's number. Matches `pickFallbackSnapshot`'s lease rule.
 *
 * Read off `getOfferDetails` rather than `getResidualInfoOverride`: that selector switches on
 * `getIsUserProgramQuotes`, and this runs while the deal is still on program-quotes, so it would return the
 * term-scoped slice instead of what the fields show.
 */
const holdLeaseResidual = (state: StateTree, pricedOnly: boolean): ProgramFallbackResidual | undefined => {
    const leaseInputs = offerReduxSelectors.getOfferDetails(state, LEASE);

    const base =
        (pricedOnly
            ? undefined
            : overriddenResidual(leaseInputs.baseResidualPercentOverride, leaseInputs.baseResidualAmountOverride)) ??
        effectiveResidual(offerReduxSelectors.getBaseResidualPercent(state), offerReduxSelectors.getBaseResidualDollar(state));

    const adjusted =
        (pricedOnly
            ? undefined
            : overriddenResidual(leaseInputs.adjustedResidualPercentOverride, leaseInputs.adjustedResidualAmountOverride)) ??
        effectiveResidual(
            offerReduxSelectors.getAdjustedResidualPercent(state),
            offerReduxSelectors.getAdjustedResidualValue(state)
        );

    if (!base && !adjusted) return undefined;

    return { ...(base && { base }), ...(adjusted && { adjusted }) };
};

/**
 * The unit the held lease rate is expressed in, so the fallback can pin it back.
 *
 * Read off the selected term first, because that is where the rate itself is read from — `getSellRate` and
 * `getRateType` both resolve through `getSelectedTerm`, so taking both from there keeps the value and its
 * unit describing the same thing. `rateTypeOverride` is the deal-level display slot and only re-pinned from
 * a payment response, so it can disagree with the term it is meant to describe: manual testing found a lease
 * whose term quoted 9.34 APR while its override still read MoneyFactor, and preferring the override wrote
 * that APR figure as a money factor — the very failure this hold exists to prevent.
 *
 * Nothing is held when the unit is neither APR nor MoneyFactor, which leaves today's behaviour untouched.
 */
/**
 * The unit the held *buy* rate is expressed in, read from the deal-level slot the buy rate itself comes
 * from, so the value and its unit describe the same thing.
 *
 * Deliberately not `holdLeaseRateType`: that reads the selected term, and the buy rate does not. The two
 * disagreeing is the case that drops the buy rate — see `writeHeldValues`.
 */
const holdBuyRateType = (state: StateTree, offerType: OfferType): ProgramFallbackRateType | undefined => {
    const unit = offerReduxSelectors.getRateTypeOverrideByOfferType(state, offerType);
    return unit === RATE_TYPE_APR || unit === RATE_TYPE_MONEY_FACTOR ? unit : undefined;
};

const holdLeaseRateType = (state: StateTree): ProgramFallbackRateType | undefined => {
    const unit = offerReduxSelectors.getRateType(state) || offerReduxSelectors.getOfferDetails(state, LEASE).rateTypeOverride;
    return unit === RATE_TYPE_APR || unit === RATE_TYPE_MONEY_FACTOR ? unit : undefined;
};

/**
 * Holds what the fallback will need, before the invalidating action reaches the reducers.
 *
 * **Call this before `next(action)`.** The rate is read off the selected term, so once a term change lands
 * the program being left is no longer selected and its rate is gone.
 *
 * The values live on the `programs` slice rather than in `penciledInputs.overridesFallbackSnapshot`: that
 * slot is one per offer type and the vehicle-switch flow already owns it, so two owners would overwrite
 * each other's snapshots. They are released as soon as the lookup's outcome is known, so a later fallback
 * cannot price with values held for an unrelated change.
 *
 * Finance holds a rate; a lease holds its residuals as well, because user-program-quotes will not price a
 * lease without them (DE765860).
 */
export const holdProgramFallbackValues = (
    store: ProgramOrchestrationStore,
    state: StateTree,
    offerType: OfferType,
    lockedProgram?: Record<string, any>,
    acqFee?: ProgramFallbackAcqFee,
    dealerInputs?: ProgramFallbackDealerInputs
): void => {
    if (offerType !== FINANCE && offerType !== LEASE) return;

    const pricedOnly = lockedProgram != null;
    const sellRateOverride = pricedOnly ? undefined : offerReduxSelectors.getSellRateOverride(state, false, offerType);
    const buyRateOverride = pricedOnly
        ? offerReduxSelectors.getBuyRate(state, false)
        : offerReduxSelectors.getBuyRateOverride(state, false, offerType);
    // Whatever the deal is pricing at, whether the user typed it or a program supplied it.
    const effectiveSellRate = offerReduxSelectors.getSellRate(state) || undefined;
    const residual = offerType === LEASE ? holdLeaseResidual(state, pricedOnly) : undefined;
    const rateType = offerType === LEASE ? holdLeaseRateType(state) : undefined;
    const pricedBuyRateType = pricedOnly ? rateType : holdBuyRateType(state, offerType);
    const buyRateType = buyRateOverride != null ? pricedBuyRateType : undefined;

    if (
        sellRateOverride == null &&
        buyRateOverride == null &&
        effectiveSellRate == null &&
        residual == null &&
        lockedProgram == null
    ) {
        return;
    }

    store.dispatch(
        holdFallbackValues({
            offerType,
            sellRateOverride,
            buyRateOverride,
            effectiveSellRate,
            residual,
            rateType,
            buyRateType,
            ...(lockedProgram && { lockedProgram }),
            ...(acqFee && { acqFee }),
            ...(dealerInputs && { dealerInputs })
        })
    );
};

/** Releases the held values once the lookup's outcome is known, so they cannot be reused later. */
export const releaseProgramFallbackValues = (store: ProgramOrchestrationStore): void => {
    store.dispatch(releaseFallbackValues());
};

export const markProgramLookupPending = (
    store: ProgramOrchestrationStore,
    trigger: string,
    offerType: OfferType,
    termMonths?: number,
    requestId?: number
): void => {
    store.dispatch(markLookupPending({ trigger, offerType, termMonths, requestId }));
};

export const resolveProgramLookup = (store: ProgramOrchestrationStore, noProgram: boolean, requestId?: number): void => {
    store.dispatch(resolveLookup(noProgram, requestId));
};

export const discardProgramLookupPending = (store: ProgramOrchestrationStore, requestId?: number): void => {
    store.dispatch(discardLookupPending(requestId));
};

export type ProgramInvalidationOptions = ProgramOrchestrationOptions & {
    /**
     * The dealer's `autoSelectProgramByDefault` setting is an on-load default, so it does not veto this
     * trigger. It says nothing about whether a person acted - the programs slide-out guard still applies.
     */
    ignoreDealerDefault?: boolean;
    lookupAction?: AnyFSA;
    keepDealerAcqFee?: boolean;
    keepDealerSecurityDeposit?: boolean;
    keepLender?: Record<string, any>;
};

export type ProgramFallbackOptions = ProgramOrchestrationOptions & {
    /**
     * Write the held values back, so user-program-quotes has something to price with. Set this when the
     * fallback follows a program lookup that produced nothing (AC7). Leave it off for a manual lender-field
     * edit, which discarded nothing and has values on screen already.
     */
    restoreHeldValues?: boolean;
    /**
     * The caller moved this deal onto program-quotes itself and keeps its own retained values, so it needs
     * the endpoint switch and nothing else.
     *
     * Two things are skipped. The programs slide-out guard, because moving the endpoint back is the caller
     * undoing its own change rather than taking someone else's. And the override seed, because
     * `penciledInputsOverrides` writes fields outside `FALLBACK_SNAPSHOT_FIELDS` — `buyRateOverride`,
     * `acqFeeOverride`, `securityDepositOverride`, `mileageChargeOverride` — which the caller's own
     * `restoreOverridesForFallback` then cannot clear, since its reducer only overwrites the keys the
     * snapshot holds.
     */
    callerOwnsEndpoint?: boolean;
    /**
     * Freeze the program's itemized lender fees into `feesOverride` before the switch (DE771282), so
     * user-program-quotes has fees to price with instead of none. Opt-in and lease-only. Two callers set it:
     * `programInvalidationMiddleware`'s manual rate/residual-edit trigger, and (DE774424) the rejected-override
     * recovery trigger — both reached while the term still holds the program that priced it. The two
     * program-lookup-outcome callers (`restoreHeldValues: true`) stay off: they reach this after a fresh
     * search already ran and failed or came up empty, where the term's dealer fees may be stale rather than
     * the leaving program's, and that path was not verified safe.
     */
    seedLenderFees?: boolean;
};

const markProgramLookup = (paymentAction: AnyFSA, seedStandards: boolean): void => {
    paymentAction.meta = {
        ...paymentAction.meta,
        [PROGRAM_QUOTES_REFETCH_META]: true,
        ...(seedStandards && { includeStandardIncentives: true })
    };
};

const dispatchEndpointSwitch = (
    store: ProgramOrchestrationStore,
    endpoint: CalculationEndpointType,
    offerType: OfferType,
    options: ProgramOrchestrationOptions,
    isProgramLookup = false,
    seedStandards = true
): void => {
    const switchAction = updateCurrentCalculationEndpoint(endpoint, offerType, {
        withPayment: options.withPayment ?? true,
        ...options.switchMeta
    }) as AnyFSA;
    if (isProgramLookup) {
        markProgramLookup(switchAction, seedStandards);
    }
    if (options.lockedProgram) {
        switchAction.meta = { ...switchAction.meta, [LOCKED_PROGRAM_META]: options.lockedProgram };
    }
    if (options.lookupEdit) {
        switchAction.meta = { ...switchAction.meta, [PROGRAM_LOOKUP_EDIT_META]: options.lookupEdit };
    }
    options.stampRequestId?.(switchAction);
    store.dispatch(switchAction);
};

const readFeeOverrides = (source: StateTree): FeeOverride[] =>
    (offerReduxSelectors.getFeesOverride(source) as FeeOverride[] | undefined) ?? [];
const isLenderFee = (fee: FeeOverride) => fee.category === FeesCategoriesType.LENDER;

const mapItemizedLenderFeeToOverride = (fee: IDealerFee): FeeOverride => ({
    category: FeesCategoriesType.LENDER,
    feeAmount: fee.dealerFeeAmount,
    feeDescription: fee.dealerFeeDesc,
    feeName: fee.dealerFeeName || fee.dealerFeeDesc,
    feeTypeId: fee.dealerFeeTypeCode,
    includeInCalcIndicator: fee.isFeeIncludedInCalculation ?? fee.includedInCalc ?? true,
    reqCapIndicator: fee.dealerFeeCapped
});

/**
 * The itemized-fee-to-override mapping and the pro-rata carve-out mirror `AutoSelectProgramScoped`'s uncheck
 * handler (AC5/AD-6) exactly. Duplicated rather than shared because that component lives in
 * `activities-desking`, which depends on this lib, not the reverse. The merge behaviour below is this
 * function's own addition, not shared with that handler — see DE774198, filed because the uncheck handler
 * still replaces `feesOverride` outright and was found to have the same drop-on-fallback bug this fixes here.
 *
 * Gated behind `DE771282_Preserve_Lender_Fees_On_Lease_Fallback`: with the toggle off, `feesOverride` is left
 * untouched and the deal keeps today's behaviour (lender fees dropped by the fallback). On, it merges rather
 * than replaces — a dealer/government override already in `feesOverride`, a lender fee the user already
 * overrode, or a lender fee the user added that the program doesn't currently supply, is not something this
 * seed owns and is carried forward as-is rather than clobbered.
 */
const seedLenderFeeOverridesFromProgram = (store: ProgramOrchestrationStore, offerType: OfferType): void => {
    if (offerType !== LEASE) return;

    const state = store.getState();
    if (!featureToggleSelectors.isLenderFeesPreservedOnLeaseFallbackEnabled(state)) return;

    const lenderFees = offerReduxSelectors.getItemizedLenderFees(state);
    if (!lenderFees?.length) return;

    store.dispatch(updatedFeesOverride(mergeLenderFeeOverrides(state, lenderFees), false));
};

/**
 * Merges the itemized lender fees into the current `feesOverride` list without clobbering anything the
 * user or dealer owns. Shared by the Lease fallback seed (DE771282) and the Finance same-program incentive
 * re-apply carry-forward (DE773854).
 */
export const mergeLenderFeeOverrides = (state: StateTree, lenderFees: IDealerFee[]): FeeOverride[] => {
    const existingOverrides = readFeeOverrides(state);
    const nonLenderOverrides = existingOverrides.filter((fee) => !isLenderFee(fee));
    const existingLenderOverridesById = new Map(existingOverrides.filter(isLenderFee).map((fee) => [fee.feeTypeId, fee]));
    const isProRataManuallyEdited = existingLenderOverridesById.has(PRO_RATA_FEE_TYPE_ID);

    const mappedLenderFees = lenderFees
        .filter((fee) => fee.dealerFeeTypeCode !== PRO_RATA_FEE_TYPE_ID || isProRataManuallyEdited)
        // A fee the user already overrode keeps their amount; only a fee they never touched gets seeded fresh.
        .map((fee) => existingLenderOverridesById.get(fee.dealerFeeTypeCode) ?? mapItemizedLenderFeeToOverride(fee));

    // A lender fee the user added that the program doesn't currently supply has no entry in `lenderFees` to
    // reconcile against, so it passes through untouched instead of being dropped by the two lines above.
    const lenderFeeTypeIds = new Set(lenderFees.map((fee) => fee.dealerFeeTypeCode));
    const userOnlyLenderOverrides = [...existingLenderOverridesById.values()].filter((fee) => !lenderFeeTypeIds.has(fee.feeTypeId));

    return [...nonLenderOverrides, ...mappedLenderFees, ...userOnlyLenderOverrides];
};

/**
 * A same-program incentive re-apply keeps `byTerm[term].program` (the rate lock), so Payment Services adds the
 * program's lender fees, including the Loan Origination Fee, whether or not the deal carried them before
 * (DE773854). The lender fees the deal shows right now are returned for that one request only; nothing is
 * written to `feesOverride`, so a later program, term or lender change cannot inherit them. With no lender
 * fees on the deal, a zero Loan Origination Fee keeps Payment Services from adding its own.
 */
export const getFrozenLenderFeeOverrides = (state: StateTree, offerType: OfferType): FeeOverride[] | undefined => {
    if (offerType !== FINANCE) return undefined;
    if (!featureToggleSelectors.isStableLenderFeesOnIncentiveReapplyEnabled(state)) return undefined;

    const lenderFees = offerReduxSelectors.getItemizedLenderFees(state);
    if (lenderFees?.length) return mergeLenderFeeOverrides(state, lenderFees).filter(isLenderFee);

    if (readFeeOverrides(state).some((fee) => isLenderFee(fee) && fee.feeTypeId === LOAN_ORIGINATION_FEE_ID)) return undefined;

    return [
        {
            category: FeesCategoriesType.LENDER,
            feeAmount: 0,
            feeName: 'Loan Origination Fee',
            feeDescription: 'Loan Origination Fee',
            feeTypeId: LOAN_ORIGINATION_FEE_ID,
            includeInCalcIndicator: false,
            reqCapIndicator: false
        }
    ];
};

// AC5 discards the values the program supplied. `partialResetOverrides` reaches wider than that: it
// replaces the whole fee-override list with the government fees, so a dealer fee the user typed goes
// with it even though no program ever supplied it. Lender fees do come from the program, so those are
// left discarded - except the acquisition fee when `retainAcqFee` is set - and everything else is put back.
export const resetProgramSuppliedOverrides = (
    store: ProgramOrchestrationStore,
    { retainAcqFee = false, retainSecurityDeposit = false }: { retainAcqFee?: boolean; retainSecurityDeposit?: boolean } = {}
): void => {
    const feesToRestore = readFeeOverrides(store.getState()).filter(
        (fee) => !isLenderFee(fee) && fee.category !== FeesCategoriesType.GOVERNMENT
    );

    const retained = {
        ...(retainAcqFee && { retainAcqFeeOverride: true }),
        ...(retainSecurityDeposit && { retainSecurityDepositOverride: true })
    };
    store.dispatch(partialResetOverrides(retained));

    if (feesToRestore.length) {
        const isKeptAfterReset = (fee: FeeOverride) => !isLenderFee(fee) || (retainAcqFee && fee.feeTypeId === ACQUISITION_FEE_ID);
        const feesAfterReset = readFeeOverrides(store.getState()).filter(isKeptAfterReset);
        store.dispatch(updatedFeesOverride([...feesAfterReset, ...feesToRestore], false));
    }
};

export const invalidateProgramAndRefetch = (
    store: ProgramOrchestrationStore,
    offerType: OfferType,
    options: ProgramInvalidationOptions = {}
): boolean => {
    const state = store.getState();

    if (!options.ignoreDealerDefault && offerReduxSelectors.getDealerForcedUpq(state)) return false;
    if (isProgramsSlideOutOpen(state)) return false;

    const lockedProgram = explicitLenderP1AppliesToOffer(state, offerType) ? options.lockedProgram : undefined;
    const hasLockedProgram = Boolean(lockedProgram);

    const keepsLender = explicitLenderP1AppliesToOffer(state, offerType) && Boolean(options.keepLender);

    if (
        !hasLockedProgram &&
        !keepsLender &&
        offerType === LEASE &&
        offerReduxSelectors.getVehicleDetails(state)?.type === AccelerateVehicleCondition.USED
    ) {
        return false;
    }

    resetProgramSuppliedOverrides(store, {
        retainAcqFee: hasLockedProgram && options.keepDealerAcqFee === true,
        retainSecurityDeposit: hasLockedProgram && options.keepDealerSecurityDeposit === true
    });

    const seedStandards = !hasLockedProgram || !offerReduxUtils.incentives.isGridIncentiveManagementEnabled(state, offerType);
    const lookupAction = hasLockedProgram || keepsLender ? options.lookupAction : undefined;
    if (lookupAction) {
        dispatchEndpointSwitch(store, CALC_ENDPOINTS.programQuotes, offerType, {
            switchMeta: options.switchMeta,
            withPayment: false
        });
        markProgramLookup(lookupAction, seedStandards);
        options.stampRequestId?.(lookupAction);
        return true;
    }

    dispatchEndpointSwitch(store, CALC_ENDPOINTS.programQuotes, offerType, { ...options, lockedProgram }, true, seedStandards);
    return true;
};

const restoreHeldAcqFee = (store: ProgramOrchestrationStore, { amount, upFront, dealerEntered }: ProgramFallbackAcqFee): void => {
    if (!dealerEntered && getManualFieldOverrides(store.getState()).includes('acqFee')) {
        store.dispatch(clearManualFieldOverrides(['acqFee']));
    }
    store.dispatch(syncedAcqFeeOverride(amount, upFront));
    const feesOverride = withAcqFeeEntry(
        readFeeOverrides(store.getState()),
        { feeAmount: amount, reqCapIndicator: !upFront },
        amount,
        !upFront
    );
    store.dispatch(updatedFeesOverride(feesOverride, false));
};

const restoreHeldDealerInputs = (
    store: ProgramOrchestrationStore,
    { securityDeposit, maxResidualizedMsrp }: ProgramFallbackDealerInputs
): void => {
    if (securityDeposit != null) {
        store.dispatch(syncedSecurityDepositOverride(securityDeposit));
    }
    if (maxResidualizedMsrp != null) {
        store.dispatch(syncedMaxResidualizedMsrpOverride(maxResidualizedMsrp, false));
    }
};

export const fallBackToUserProgramQuotes = (
    store: ProgramOrchestrationStore,
    offerType: OfferType,
    options?: ProgramFallbackOptions
): boolean => {
    const state = store.getState();

    if (offerReduxSelectors.getIsUserProgramQuotes(state, offerType)) return false;
    if (!options?.callerOwnsEndpoint && isProgramsSlideOutOpen(state)) return false;

    const hold = options?.restoreHeldValues ? getProgramFallbackHold(state, offerType) : undefined;
    const appliesExplicitLender = explicitLenderP1AppliesToOffer(state, offerType);
    const lockedProgram = appliesExplicitLender ? options?.lockedProgram ?? hold?.lockedProgram : undefined;
    const heldAcqFee = appliesExplicitLender ? hold?.acqFee : undefined;
    const heldDealerInputs = appliesExplicitLender ? hold?.dealerInputs : undefined;
    const heldWinsOverSeed = appliesExplicitLender && hold?.lockedProgram != null;

    if (!options?.callerOwnsEndpoint) {
        if (heldWinsOverSeed) restoreHeldValues(store, offerType, true);
        store.dispatch(setProgramFallbackOverrides());
        if (options?.seedLenderFees) seedLenderFeeOverridesFromProgram(store, offerType);
        if (heldAcqFee) restoreHeldAcqFee(store, heldAcqFee);
        if (heldDealerInputs) restoreHeldDealerInputs(store, heldDealerInputs);
    }

    // The seed reads from the program, and the case that brings us here is usually that there is no program
    // to read. The held values are then the only ones user-program-quotes can price with, so they land after
    // the seed rather than before it.
    const restored = options?.restoreHeldValues ? restoreHeldValues(store, offerType, heldWinsOverSeed) : undefined;

    dispatchEndpointSwitch(store, CALC_ENDPOINTS.userProgramQuotes, offerType, { ...options, lockedProgram });

    // The same write, once on each side of the switch, because the slot it lands in depends on the endpoint
    // and the endpoint changes in between:
    //   before — still on program-quotes, so `handleRateCases` writes only `overridesByTerm[months].sellRate`,
    //            and `handleResidualCases` only `overridesByTerm[months].residualInfo`. Those are the slots
    //            the payment payload reads, so they are what the switch's own payment prices with.
    //   after  — now on user-program-quotes, so the same actions also write the flat `sellRateOverride` and
    //            `baseResidual*Override`/`adjustedResidual*Override` — the slots the fields display and the
    //            slots `hasRequiredFieldsForUPQ` checks. They carry no payment of their own.
    // On finance with the payment grid on, `handleCalculationEngineChange` promotes the term-scoped value into
    // the flat slot as well, so this second write is redundant there — but it is the only thing that does it
    // when the grid is off.
    if (restored) writeHeldValues(store, restored);

    return true;
};

type HeldValues = {
    months: number;
    sellRate?: number;
    /**
     * The rate already overridden at `months`, which the restore leaves as it is. Carried anyway because
     * `rateType` only reaches the payload on a sell-rate write, while `PARTIAL_RESET_OVERRIDES` clears
     * `rateTypeOverride` whether or not the value survived it — so a restore that had no value to give back
     * left the unit undeclared and the payload guessed `DEFAULT_RATE_TYPE_MAPPING.lease` (DE766422). Only set
     * when it equals the held rate, so the write carries the unit without changing the figure.
     */
    sellRateToRedeclare?: number;
    buyRate?: number;
    /** The unit `buyRate` is in, which is not `rateType` — see `ProgramFallbackHold.buyRateType`. */
    buyRateType?: ProgramFallbackRateType;
    residual?: ProgramFallbackResidual;
    rateType?: ProgramFallbackRateType;
};

/**
 * Writes the held values as overrides, so user-program-quotes has the inputs it will not price without.
 *
 * A value the user entered is restored as-is. Otherwise what the deal was pricing at is written, which came
 * from the program just left — that is the payment AC8 calls "estimated, not lender-backed".
 *
 * `alreadyOverridden` reads the term-scoped slot alone: the restore runs on program-quotes, where
 * `getSellRateOverride`'s deal-level branch is gated out, so a `??` rung through it would resolve back to
 * this same slot.
 *
 * The unit is redeclared only when the value already on the deal IS the held one. `rateType` describes the
 * held rate and nothing else, while `alreadyOverridden` is read after `PARTIAL_RESET_OVERRIDES`, whose
 * `retainedSellRate` may be a rate entered at another term or carried from the deal-level slot under a
 * different unit. `isImplausibleMoneyFactor` would not catch that pairing: it refuses an APR-scaled figure
 * labelled MoneyFactor, never a money-factor-scaled figure labelled APR, so a mismatch that way round would
 * be written and price the lease at 0.0026% APR (DE762937 with the operands swapped).
 */
const restoreHeldValues = (
    store: ProgramOrchestrationStore,
    offerType: OfferType,
    heldWinsOverSeed: boolean
): HeldValues | undefined => {
    if (offerType !== FINANCE && offerType !== LEASE) return undefined;

    const state = store.getState();
    // The term is read after the change while the values were held before it, so on a 60 → 72 change what
    // applied at 60 is written as an override at 72. That is deliberate: the fallback is happening
    // *because* no program covers 72, so nothing was ever quoted there, and the held values are the only
    // inputs user-program-quotes can price with. Writing them against the term being left would price nothing.
    // The result is the payment AC8 calls estimated rather than lender-backed.
    const months = offerReduxSelectors.getSelectedTermMonths(state, offerType);
    if (months == null) return undefined;

    const held = getProgramFallbackHold(state, offerType);
    if (!held) return undefined;

    const alreadyOverridden = heldWinsOverSeed
        ? undefined
        : offerReduxSelectors.getSellRateOverrideByOfferTypeAndTerm(state, offerType, months);

    const rateType = offerType === LEASE ? held.rateType : undefined;
    const heldSellRate = held.sellRateOverride ?? held.effectiveSellRate;
    const values: HeldValues = {
        months,
        sellRate: alreadyOverridden != null ? undefined : heldSellRate,
        sellRateToRedeclare: rateType && alreadyOverridden != null && alreadyOverridden === heldSellRate ? heldSellRate : undefined,
        buyRate: held.buyRateOverride,
        buyRateType: held.buyRateType,
        residual: offerType !== LEASE ? undefined : heldWinsOverSeed ? held.residual : residualStillNeeded(state, held.residual),
        rateType
    };
    if (values.sellRate == null && values.sellRateToRedeclare == null && values.buyRate == null && values.residual == null) {
        return undefined;
    }

    writeHeldValues(store, values);
    return values;
};

/**
 * Narrows the held residual to the halves the seed did not already supply, the way `alreadyOverridden` does
 * for the rate. The seed reads from the program, so it usually supplies nothing here — but when it does
 * supply a value that value describes the term the deal is on now, and the held one does not.
 */
const residualStillNeeded = (state: StateTree, held: ProgramFallbackResidual | undefined): ProgramFallbackResidual | undefined => {
    if (!held) return undefined;

    const leaseInputs = offerReduxSelectors.getOfferDetails(state, LEASE);
    const hasBase = overriddenResidual(leaseInputs.baseResidualPercentOverride, leaseInputs.baseResidualAmountOverride) != null;
    const hasAdjusted =
        overriddenResidual(leaseInputs.adjustedResidualPercentOverride, leaseInputs.adjustedResidualAmountOverride) != null;

    const base = hasBase ? undefined : held.base;
    const adjusted = hasAdjusted ? undefined : held.adjusted;

    if (!base && !adjusted) return undefined;

    return { ...(base && { base }), ...(adjusted && { adjusted }) };
};

/**
 * Writes the held rate back, with the unit it was quoted in.
 *
 * Without `rateType` a lease reverts to `DEFAULT_RATE_TYPE_MAPPING.lease` (MoneyFactor) and an APR rate is
 * sent as a money factor. `pinDealLevelRateType` is what gets the unit into the deal-level slot the payload
 * reads while the deal is still on program-quotes; the value stays out of that slot until the switch lands.
 *
 * A rate already on the deal is written back unchanged rather than skipped, because the unit only travels on
 * the value's own write — see `sellRateToRedeclare`.
 */
const writeHeldRate = (store: ProgramOrchestrationStore, values: HeldValues): void => {
    const { months, sellRate, sellRateToRedeclare, rateType } = values;

    const rate = sellRate ?? sellRateToRedeclare;
    if (rate == null) return;

    store.dispatch(
        updateSellRateOverride(rate, months, {
            isSelectedTerm: true,
            skipPaymentCall: true,
            rateType,
            ...(rateType && { pinDealLevelRateType: true })
        })
    );
};

/**
 * Never fires a payment of its own — the endpoint switch owns the one call that prices the result.
 *
 * **The buy rate declares its own unit, and that is what keeps it.** Left undeclared,
 * `UPDATED_BUY_RATE_OVERRIDE` resolves `declaredRateType` from the deal-level slot `writeHeldRate` pins, so a
 * MoneyFactor held unit against an APR-scaled `held.buyRateOverride` makes `isImplausibleMoneyFactor` true
 * and the reducer returns state — the buy rate is silently dropped. The held sell-rate unit describes the
 * selected term, not the deal-level buy rate, so it must not adjudicate it. A rate travelling without its
 * buy rate is the worse failure: Payment Services then defaults one from the deal and can land it above the
 * sell rate (see `maySendBuyRate`).
 *
 * Ordering is not a substitute for declaring the unit, and writing the buy rate first does not work.
 * `UPDATED_SELL_RATE_OVERRIDE` runs `withoutImplausibleBuyRate` over the term entry under the same
 * predicate, so a buy rate written first is stripped by the sell write instead of being refused by its own.
 */
const writeHeldValues = (store: ProgramOrchestrationStore, values: HeldValues): void => {
    const { months, buyRate, buyRateType, residual } = values;

    writeHeldRate(store, values);
    if (buyRate != null) {
        // This creator takes withPayment rather than skipPaymentCall, but the intent is the same.
        store.dispatch(updateBuyRateOverride(buyRate, months, { isSelectedTerm: true, withPayment: false, rateType: buyRateType }));
    }
    // The base write also sets `programResidual*Override` and `baseResidualTypeOverride`, and the adjusted
    // write sets `adjustedResidualTypeOverride` — between them every field `hasRequiredFieldsForUPQ` reads
    // for a lease. `updateResidualTabDisplayType` is deliberately not used: it hard-codes a payment call.
    if (residual?.base) {
        store.dispatch(updateBaseResidualOverride({ value: residual.base.value, months, unitType: residual.base.unitType }, false));
    }
    if (residual?.adjusted) {
        store.dispatch(
            updateAdjustedResidualOverride({ value: residual.adjusted.value, months, unitType: residual.adjusted.unitType }, false)
        );
    }
};
